import { useState } from 'react';
import { useSelectedContract } from 'hooks';
import { Pages } from 'constants/menu';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  FormCondition,
  Condition,
  ConditionType,
  TimeUnit,
  SignboardRemoval,
  SignboardRemovalRemovalType,
  StaffGetContractSettlementResponse,
  StaffUpdateContractSettlementRequest,
  useStaffGetContractDates,
  useInvalidateUseStaffGetContractSettlement,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import { useQueryClient } from '@tanstack/react-query';
import {
  AgxBadge,
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxRadioGroup,
  AgxDivider,
  AgxSpacer,
  AgxTimePicker,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import ConditionDate from './components/ConditionDate';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import slugify from 'slugify';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';
import './panels/ConditionDatePicker.scss';

const settlementMinuteValues = Array.from({ length: 60 }, (_, minute) =>
  minute.toString().padStart(2, '0')
);

const Settlement = () => {
  const { contract, campaignId } = useSelectedContract();
  const [showSettlementTimeValidation, setShowSettlementTimeValidation] = useState(false);
  const invalidateUseStaffGetContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const queryClient = useQueryClient();

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  );

  const {
    data: contractSettlement,
    isPending,
    isError,
  } = useStaffGetContractSettlement(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { data: contractDates } = useStaffGetContractDates(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  type SettlementDraft = {
    settlementInfo: FormCondition | undefined;
    signboardRemoval: SignboardRemoval | undefined;
  };

  const toDraft = (settlement: StaffGetContractSettlementResponse): SettlementDraft => ({
    settlementInfo: settlement.settlementInfo,
    signboardRemoval: settlement.signboardRemoval,
  });

  const source = contractSettlement ? toDraft(contractSettlement) : undefined;

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:settlement`,
    source,
    isPending,
    isError,
    persist: async ({ draft }) => {
      if (draft.settlementInfo?.timeOfDay === '') {
        setShowSettlementTimeValidation(true);
        return { accepted: false, errors: ['Enter a settlement time.'] };
      }

      const request: StaffUpdateContractSettlementRequest = {
        settlementInfo: draft.settlementInfo,
        signboardRemoval: draft.signboardRemoval,
      };
      const response = await staffUpdateContractSettlement({
        campaignId: campaignId!,
        contractId: contract!.contractId,
        body: request,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.contractSettlement)
        throw new Error('Canonical contract settlement is required');

      setShowSettlementTimeValidation(false);

      queryClient.setQueryData<StaffGetContractSettlementResponse>(
        ['staffGetContractSettlement', campaignId!, contract!.contractId],
        response.contractSettlement
      );

      if (!rexListing && isPendingRexListing === false) {
        openWithChanges(
          !isEqual(prevSettlementDueDate.dueDateString, currSettlementDueDate.dueDateString)
            ? [
                {
                  field: RexDisconnectedModalFields.SettlementDate,
                  value: currSettlementDueDate.dueDateString,
                },
              ]
            : []
        );
      }

      return { accepted: true, baseline: toDraft(response.contractSettlement) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () =>
        invalidateUseStaffGetContractSettlement(campaignId!, contract!.contractId),
    },
  });

  const prevSettlementDueDate = useFormConditionDueDate(
    source?.settlementInfo,
    contractDates?.contractDate
  );
  const currSettlementDueDate = useFormConditionDueDate(
    editor.draft?.settlementInfo,
    contractDates?.contractDate
  );
  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.SETTLEMENT} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { settlementInfo, signboardRemoval } = editor.draft;
  const canAddSettlementTime = Boolean(currSettlementDueDate.dueDateString);

  const updateSettlementTime = (timeOfDay: string | undefined) => {
    editor.update((current) => ({
      ...current,
      settlementInfo: {
        ...current.settlementInfo!,
        timeOfDay,
      },
    }));
  };

  const settlementBadgeText = (() => {
    if (!settlementInfo) return '';

    if (
      settlementInfo.condition === Condition.NdaysFromDate &&
      settlementInfo.conditionType === ConditionType.Contract &&
      settlementInfo.daysFromDate &&
      settlementInfo.daysFromDate > 0 &&
      (settlementInfo.fromDateTimeUnit === TimeUnit.Days ||
        settlementInfo.fromDateTimeUnit === TimeUnit.Weeks)
    ) {
      return `${settlementInfo.daysFromDate} ${settlementInfo.fromDateTimeUnit.toLowerCase()} from contract date`;
    }

    return currSettlementDueDate.dueDateString
      ? dayjs(currSettlementDueDate.dueDateString).format('Do MMM')
      : '';
  })();

  const settlementDate = dateOnlyToDate(currSettlementDueDate.dueDateString);

  return (
    <ContractInformationPage currentPage={Pages.SETTLEMENT} editor={editor}>
      <AgxCollapsible
        key={`SettlementPanel${isPending ? 'Loaded' : ''}`}
        variant="withDivider"
        enableToggle
        open
        customTitle={
          <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
            <AgxTypography
              id={`condition-${slugify('Settlement')}`}
              type="title"
              size="large"
              color="var(--text-primary)"
            >
              Settlement
            </AgxTypography>
            {settlementBadgeText && (
              <AgxBadge type="colourfill" colour="yellow" size="md" text={settlementBadgeText} />
            )}
          </AgxSpacer>
        }
        content={
          <>
            {settlementInfo && (
              <>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-md"
                  fill
                  padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
                >
                  <ConditionDate
                    id="settlementInfo"
                    condition={settlementInfo}
                    specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
                  />
                </AgxSpacer>
                <AgxDivider variant="horizontal" />
              </>
            )}
            <AgxSpacer
              direction="vertical"
              gap="spacing-xl"
              fill
              padding={{ top: 'spacing-3xl', bottom: 'spacing-3xl' }}
              contentAlignment="start"
            >
              <AgxDatePicker
                id="settlementInfo-due-date"
                className="contract-information-date-picker"
                optionalText=""
                label="Date"
                value={settlementDate ? dayjs(settlementDate).format('YYYY-MM-DD') : ''}
                onChange={(value) => {
                  editor.update((current) => ({
                    ...current,
                    settlementInfo: {
                      condition: Condition.OnSpecificDate,
                      conditionType: ConditionType.Contract,
                      referenceDate: value ? toDateOnlyString(value) : undefined,
                      completedDate: current.settlementInfo?.completedDate,
                      timeOfDay: current.settlementInfo?.timeOfDay,
                    },
                  }));
                }}
              />
            </AgxSpacer>
            {canAddSettlementTime && (
              <AgxSpacer gap="spacing-md" padding={{ bottom: 'spacing-3xl' }}>
                {settlementInfo?.timeOfDay !== undefined ? (
                  <AgxTimePicker
                    id="settlement-time"
                    label="Time"
                    value={settlementInfo?.timeOfDay ?? undefined}
                    required
                    minuteValues={settlementMinuteValues}
                    requireMeridianSelection
                    showValidation={showSettlementTimeValidation}
                    inputAction={
                      <AgxButton
                        id="settlement-time-remove"
                        variant="hollow"
                        text="Remove"
                        onClick={() => {
                          setShowSettlementTimeValidation(false);
                          updateSettlementTime(undefined);
                        }}
                      />
                    }
                    onChange={(timeOfDay) => {
                      if (!timeOfDay) return;

                      setShowSettlementTimeValidation(false);
                      updateSettlementTime(timeOfDay);
                    }}
                  />
                ) : (
                  <AgxButton
                    id="settlement-time-add"
                    variant="primary"
                    text="Add settlement time"
                    onClick={() => {
                      setShowSettlementTimeValidation(false);
                      updateSettlementTime('');
                    }}
                  />
                )}
              </AgxSpacer>
            )}
          </>
        }
      />
      <SubPagePanel
        key={`SignboardRemovalPanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Signboard Removal"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          id="signboardRemoval_RemovalType"
          key={`signboardRemoval_RemovalType${isPending ? 'Loaded' : ''}`}
          layout="stacked"
          value={signboardRemoval?.removalType || ''}
          onChange={(val) => {
            if (val === signboardRemoval?.removalType) return;
            editor.update((current) => ({
              ...current,
              signboardRemoval: {
                ...current.signboardRemoval,
                removalType: val as SignboardRemovalRemovalType,
              },
            }));
          }}
          options={[
            {
              label: 'Upon settlement',
              value: SignboardRemovalRemovalType.UponSettlement,
            },
            {
              label: 'On Specific Date',
              value: SignboardRemovalRemovalType.OnSpecificDate,
            },
            {
              label: 'No signboard installed',
              value: SignboardRemovalRemovalType.NoSignboardInstalled,
            },
          ]}
          subContent={
            signboardRemoval?.removalType === SignboardRemovalRemovalType.OnSpecificDate
              ? [
                  <AgxSpacer
                    direction="vertical"
                    key={`signboardRemoval_specificDate_Column${isPending ? 'Loaded' : ''}`}
                    gap="spacing-md"
                    padding={{
                      top: 'spacing-xl',
                      bottom: 'spacing-xl',
                      left: 'spacing-xl',
                      right: 'spacing-xl',
                    }}
                  >
                    <AgxTextInput
                      id="signboardRemoval_specificDate"
                      key={`signboardRemoval_specificDate${isPending ? 'Loaded' : ''}`}
                      onInputValueChange={({ value }) => {
                        editor.update((current) => ({
                          ...current,
                          signboardRemoval: {
                            ...current.signboardRemoval,
                            specificDate: value || undefined,
                          },
                        }));
                      }}
                      required
                      hideOptionalText
                      type="date"
                      defaultValue={
                        signboardRemoval?.specificDate
                          ? dayjs(signboardRemoval?.specificDate).format('YYYY-MM-DD')
                          : undefined
                      }
                      errorText={
                        editor.dirty && !signboardRemoval?.specificDate ? 'Enter a date' : undefined
                      }
                    />
                  </AgxSpacer>,
                ]
              : undefined
          }
        />
      </SubPagePanel>
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default Settlement;
